import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { verifyOtp } from "../../api/authApi";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../ChatPage/hooks/useTheme";
import { themes, createStyles } from "../ChatPage/styles";

export default function VerifyPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const { themeName } = useTheme();
    const theme = themeName === "light" ? themes.light : themes.dark;
    const styles = createStyles(theme);

    const phoneNumber = location.state?.phoneNumber;

    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!phoneNumber) {
            navigate("/register");
        }
    }, [phoneNumber, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (code.length !== 6) {
            setError("Please enter a 6-digit code");
            return;
        }

        setLoading(true);
        try {
            const response = await verifyOtp(phoneNumber, code);
            login(response.accessToken);

            // Get the chat URL from localStorage or navigate to default
            const returnUrl = localStorage.getItem('returnUrl');
            if (returnUrl) {
                localStorage.removeItem('returnUrl');
                navigate(returnUrl);
            } else {
                // Navigate back to where they came from (should be chat page)
                navigate(-3);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, "");
        if (value.length <= 6) {
            setCode(value);
        }
    };

    return (
        <div style={pageStyle(theme)}>
            <div style={containerStyle(theme)}>
                <h1 style={titleStyle}>Підтвердження</h1>

                <p style={descriptionStyle(theme)}>
                    Введіть 6-значний код, надісланий на номер{" "}
                    <strong>{phoneNumber}</strong>
                </p>

                <form onSubmit={handleSubmit} style={formStyle}>
                    <input
                        type="text"
                        inputMode="numeric"
                        placeholder="000000"
                        value={code}
                        onChange={handleCodeChange}
                        style={{
                            ...inputStyle(theme),
                            textAlign: "center",
                            fontSize: "24px",
                            letterSpacing: "8px",
                            fontWeight: "600",
                        }}
                        disabled={loading}
                        maxLength={6}
                        autoFocus
                    />

                    {error && <div style={errorStyle}>{error}</div>}

                    <button
                        type="submit"
                        disabled={loading || code.length !== 6}
                        style={{
                            ...styles.button,
                            width: "100%",
                            ...(loading || code.length !== 6 ? styles.disabledButton : {}),
                        }}
                    >
                        {loading ? "Перевірка..." : "Підтвердити"}
                    </button>
                </form>

                <div style={linkContainerStyle}>
                    <button
                        onClick={() => navigate(-1)}
                        style={linkButtonStyle(theme)}
                        disabled={loading}
                    >
                        Назад
                    </button>
                </div>
            </div>
        </div>
    );
}

const pageStyle = (theme) => ({
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: theme.bg,
    color: theme.text,
    padding: "20px",
});

const containerStyle = (theme) => ({
    width: "100%",
    maxWidth: "400px",
    padding: "32px 24px",
    borderRadius: "16px",
    background: theme.panel,
    border: `1px solid ${theme.border}`,
});

const titleStyle = {
    fontSize: "24px",
    fontWeight: "800",
    marginBottom: "16px",
    textAlign: "center",
};

const descriptionStyle = (theme) => ({
    fontSize: "14px",
    color: theme.muted,
    textAlign: "center",
    marginBottom: "24px",
    lineHeight: "1.5",
});

const formStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
};

const inputStyle = (theme) => ({
    padding: "16px",
    fontSize: "16px",
    border: `1px solid ${theme.border}`,
    borderRadius: "12px",
    background: theme.inputBg,
    color: theme.text,
    outline: "none",
});

const errorStyle = {
    color: "#ff4444",
    fontSize: "14px",
    textAlign: "center",
};

const linkContainerStyle = {
    marginTop: "20px",
    textAlign: "center",
};

const linkButtonStyle = (theme) => ({
    fontSize: "14px",
    color: theme.buttonBg,
    background: "none",
    border: "none",
    cursor: "pointer",
    textDecoration: "underline",
    padding: "0",
});
